import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import styles from "./SeriesCard.module.css";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const SeriesCard = ({ series, delay = 0 }: any) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  // Get all product images from the series
  const pictures = useMemo(() => {
    return series.models?.map((model: any) => model.picture[0].path) || [];
  }, [series.models]);

  // Auto-play with hover control
  useEffect(() => {
    if (pictures.length <= 1) return;

    const startInterval = () => {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % pictures.length);
      }, 3000);
    };

    if (!isHovering) {
      startInterval();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, pictures.length]);

  const handleCardClick = () => {
    navigate(`/series/${series.id}`);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const cardVariants: any = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: delay / 1000,
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className={styles.card}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={handleCardClick}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{
        y: -8,
        transition: { duration: 0.3 },
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.imageContainer}>
          <div className={styles.imageCarousel}>
            {pictures.map((image: string, index: number) => (
              <img
                key={index}
                src={image}
                alt={series.title}
                loading="lazy"
                onLoad={index === 0 ? handleImageLoad : undefined}
                className={`${styles.seriesImage} ${
                  index === currentImageIndex ? styles.activeImage : ""
                } ${imageLoaded ? styles.loaded : ""}`}
              />
            ))}
          </div>

          {/* Loading Skeleton */}
          {!imageLoaded && <div className={styles.imageSkeleton}></div>}

          {pictures.length > 1 && (
            <div className={styles.carouselIndicators}>
              {pictures.map((_: string, index: number) => (
                <button
                  key={index}
                  className={`${styles.indicator} ${
                    index === currentImageIndex ? styles.activeIndicator : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Hover Overlay */}
          <div className={styles.hoverOverlay}>
            <div className={styles.overlayContent}>
              <span>View Series</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{series.title}</h3>
        <p className={styles.description}>{series.description}</p>
        <div className={styles.footer}>
          <div className={styles.modelsCount}>
            {series.models?.length || 0} Models
          </div>
          <div className={styles.features}>
            {series.features
              ?.slice(0, 2)
              .map((feature: string, index: number) => (
                <span key={index} className={styles.featureTag}>
                  {feature}
                </span>
              ))}
          </div>
        </div>
      </div>

      {/* Gradient Border Effect */}
      <div className={styles.cardBorder}></div>
    </motion.div>
  );
};

export default memo(SeriesCard);
